import React, { useState } from 'react';
import { Controller, useFieldArray, useWatch } from 'react-hook-form';
import { Alert } from '../../../../CommonComponents/OPL/Alert/Alert';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../CommonComponents/OPL/Button/Button';
import { Input } from '../../../../CommonComponents/OPL/Input/Input';
import { InputNumberMask } from '../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Select } from '../../../../CommonComponents/OPL/Select/Select/Select';
import { UPSERT_ASSET_FORM_ID } from '../../components/UpsertAssetModal/UpsertAssetModal.meta';
import { BusinessAssetDTO } from '../../tables/Business/Business.meta';
import { useBusinessAssetsForm } from './useBusinessAssetsForm';
import { NetProceedsFromSale } from './components/NetProceedsFromSale/NetProceedsFromSale';
import { Toggle } from '../../../../CommonComponents/OPL/Toggle/Toggle';
import { OtherAssetsDTO } from '../../api/useOtherAssetsQuery';
import { AssetBucketSelect } from '../components/AssetBucketSelect/AssetBucketSelect';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { Radio } from '../../../../CommonComponents/OPL/Radio/Radio';
import { GrossEstateCheckbox } from '../components/GrossEstateCheckbox/GrossEstateCheckbox';
import { getIsOwnerTrust } from '../utils/getIsOwnerTrust';
import { BusinessAssetsFormValues } from './BusinessAssetsForm.meta';
import { ManagementStyle } from '../components/ManagementStyle/ManagementStyle';
import { AssetOwnership } from '../components/AssetOwnership/AssetOwnership';
import { APPEND_ASSET_OWNER, OwnerModelFormDTO } from '../../OtherAssets.meta';
import { HelperText } from '../../../../CommonComponents/OPL/HelperText/HelperText';

export interface Props {
  clientId: string;
  asset: BusinessAssetDTO | null;
  options: OtherAssetsDTO['Options'];
  onSuccess: () => void;
  isEstatePlanningWorkflow: boolean;
}

export const BusinessAssetsForm = ({
  clientId,
  options,
  asset,
  onSuccess,
  isEstatePlanningWorkflow,
}: Props) => {
  const {
    data: {
      formBag: {
        register,
        control,
        formState: { errors },
        watch,
        setValue,
      },
      mutationBag,
      ownerOptions,
      saleYearOptions,
      defaultShowAdvanced,
      assetBucketOptions,
      assetBucketingByManagementStyle,
      managementStylesOptions,
      onManagementSelect,
      allowAssetBucketingCustomization,
      displayAssetBucketing,
    },
    actions: { onSubmit },
  } = useBusinessAssetsForm({ clientId, options, asset, onSuccess });

  const [showAdvanced, setShowAdvanced] = useState(defaultShowAdvanced);

  const [ownerModel, watchedManagementStyleSelection, useUnderlyingAssetsForValue] = watch([
    'ownerModel',
    'orionManagementStyleId',
    'useUnderlyingAssetsForValue',
  ]);

  const isOwnerTrust =
    ownerModel.length === 1 ? getIsOwnerTrust(ownerModel[0].Name ?? '', ownerOptions) : false;

  const { fields, update, append, remove } = useFieldArray<
    BusinessAssetsFormValues,
    'ownerModel',
    'fieldKey'
  >({
    control,
    name: 'ownerModel',
    keyName: 'fieldKey',
  });

  const watchOwner = useWatch({ control, name: 'ownerModel' });

  const ownerModelFields = fields.map((field, index) => ({
    ...field,
    ...watchOwner[index],
  }));

  const updateOwner = (index: number, data: OwnerModelFormDTO) => {
    update(index, {
      ...ownerModelFields[index],
      ...data,
    });
  };

  const setPercentage = (percentArr: number[]) => {
    ownerModelFields.map((field, index) => {
      setValue(`ownerModel.${index}.Percentage`, percentArr[index].toString());
    });
  };

  return (
    <form
      onSubmit={onSubmit}
      id={UPSERT_ASSET_FORM_ID}
      css={`
        display: flex;
        flex-direction: column;
        gap: ${({ theme }) => theme.space['16']}px;
      `}
      aria-label="Business assets form"
    >
      {mutationBag.error && (
        <Alert
          variant="error"
          message={mutationBag.error.message}
          onRequestClose={mutationBag.reset}
        />
      )}

      <Input
        label="Name"
        formVariant={errors.name && 'error'}
        helperText={errors.name?.message}
        maxLength={32}
        {...register('name')}
      />

      <AssetOwnership<BusinessAssetsFormValues>
        control={control}
        options={options.BusinessAssetOptions.ownerOptions}
        allowsUnequalOwnership={options.BusinessAssetOptions.AllowUnequalOwnership}
        ownerModelFields={ownerModelFields}
        updateOwner={updateOwner}
        appendOwner={() => append({ ...APPEND_ASSET_OWNER })}
        removeOwner={(index: number) => remove(index)}
        setPercentage={setPercentage}
        error={errors?.ownerModel ? errors?.ownerModel[0]?.Id?.message : ''}
      />

      <Box
        css={`
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: ${({ theme }) => theme.space['16']}px;
        `}
      >
        <Controller
          control={control}
          name="value"
          render={({ field: { onChange, ...field } }) => (
            <InputNumberMask
              {...field}
              label="Value"
              decimalScale={2}
              allowNegative={false}
              leftIcon="far fa-dollar-sign"
              formVariant={errors.value && 'error'}
              helperText={errors.value?.message}
              onValueChange={({ floatValue }) => {
                onChange(floatValue ?? null);
              }}
              disabled={useUnderlyingAssetsForValue}
            />
          )}
        />

        <Controller
          control={control}
          name="yearlyIncome"
          render={({ field: { onChange, ...field } }) => (
            <InputNumberMask
              label="Pre-tax Profit"
              decimalScale={2}
              allowNegative={false}
              leftIcon="far fa-dollar-sign"
              innerText="/year"
              onValueChange={({ floatValue }) => {
                onChange(floatValue ?? null);
              }}
              {...field}
            />
          )}
        />
      </Box>

      {isEstatePlanningWorkflow && !isOwnerTrust && <GrossEstateCheckbox control={control} />}

      {assetBucketingByManagementStyle && (
        <ManagementStyle<BusinessAssetsFormValues>
          control={control}
          options={managementStylesOptions}
          formKey="orionManagementStyleId"
          onManagementSelect={onManagementSelect}
        />
      )}

      {displayAssetBucketing && (
        <AssetBucketSelect<BusinessAssetsFormValues>
          control={control}
          formKey="customBucketIndex"
          isDisabled={
            (assetBucketingByManagementStyle && !allowAssetBucketingCustomization) ||
            (assetBucketingByManagementStyle && watchedManagementStyleSelection === null)
          }
          options={assetBucketOptions}
        />
      )}

      {isEstatePlanningWorkflow && (
        <Box>
          <Text as="label" variant="formLabel" color="neutral.600">
            Liquidity Status
          </Text>

          <Box display="flex" flexDirection="column" gridGap="8" mb="8">
            <Box>
              <Controller
                name="isLiquid"
                control={control}
                render={({ field: { value, name, onChange } }) => (
                  <>
                    <Radio name={name} checked={value} onChange={() => onChange(true)}>
                      Liquid
                    </Radio>
                    <Radio name={name} checked={!value} onChange={() => onChange(false)} mr="4">
                      Illiquid
                    </Radio>
                  </>
                )}
              />
            </Box>
          </Box>
        </Box>
      )}

      <Button
        variant="link"
        leftIcon={showAdvanced ? 'fas fa-chevron-down' : 'fas fa-chevron-right'}
        onClick={() => setShowAdvanced(!showAdvanced)}
        aria-expanded={showAdvanced}
        size="small"
        mr="auto"
      >
        {showAdvanced ? 'Hide' : 'Show'} Advanced
      </Button>

      {showAdvanced && (
        <>
          <Box
            css={`
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: ${({ theme }) => theme.space['16']}px;
            `}
          >
            <Controller
              control={control}
              name="assetGrowth"
              render={({ field: { onChange, ...field } }) => (
                <InputNumberMask
                  label="Asset Growth Rate"
                  tooltip="The rate at which the asset’s market value will grow."
                  rightIcon="far fa-percent"
                  decimalScale={2}
                  allowNegative={false}
                  onValueChange={({ floatValue }) => {
                    onChange(floatValue ?? null);
                  }}
                  {...field}
                />
              )}
            />

            <Controller
              name="saleYear"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Select
                  label="Sale Year"
                  value={value ? ({ value, label: value } as any) : null}
                  options={saleYearOptions}
                  onChange={option => onChange(option?.value || null)}
                  tooltip="If applicable, enter the year this business will be sold."
                  isPortal
                  isClearable
                />
              )}
            />
          </Box>

          <Box mx={-24}>
            <NetProceedsFromSale control={control} />
          </Box>

          <Controller
            control={control}
            name="incomeGrowth"
            render={({ field: { onChange, ...field } }) => (
              <InputNumberMask
                label="Income Growth"
                rightIcon="far fa-percent"
                decimalScale={2}
                allowNegative={false}
                onValueChange={({ floatValue }) => {
                  onChange(floatValue ?? null);
                }}
                {...field}
              />
            )}
          />

          <Controller
            name="assetEarmarks"
            control={control}
            render={({ field: { value, onChange } }) => (
              <Select
                isMulti
                label="Asset Earmark"
                placeholder="Select a goal..."
                value={value}
                options={options.BusinessAssetOptions.EarmarkOptions}
                onChange={earmarks => onChange(earmarks)}
                noOptionsMessage={() => 'There are no earmarks left to add'}
                closeMenuOnSelect={false}
                isClearable
                isPortal
              />
            )}
          />

          <Toggle
            {...register('selfEmployed')}
            size="small"
            helperText="Applies SECA tax and deducts half the amount from pre-tax profit."
          >
            SECA Tax
          </Toggle>

          <Box borderBottom="1px solid" borderColor="neutral.200" mx={-24} />

          <Box>
            <Text as="label" variant="formLabel" color="neutral.600">
              Net Worth Reporting
            </Text>
            <HelperText mb="8">
              Choose how the business value should be reported for net worth and estate planning
              purposes
            </HelperText>
            <Controller
              control={control}
              name="useUnderlyingAssetsForValue"
              render={({ field: { value, name, onChange } }) => (
                <Box display="flex" flexDirection="column" mb="8" css={{ gap: 8 }}>
                  <Radio
                    name={name}
                    checked={value}
                    onChange={() => onChange(true)}
                    mr="4"
                    helperText="Calculate business values from underlying assets & liabilities owned by this business"
                  >
                    Use underlying assets
                  </Radio>

                  <Radio
                    name={name}
                    checked={!value}
                    onChange={() => onChange(false)}
                    helperText="Use the value entered in this business profile"
                  >
                    Use manual value
                  </Radio>
                </Box>
              )}
            />
          </Box>
        </>
      )}
    </form>
  );
};
